import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  FileText, 
  User, 
  BarChart3,
  Bell,
  LogOut
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { Button } from './ui/button';
import NewClientModal from './NewClientModal';
import DemoBanner from './DemoBanner';
import DemoRouteWatcher from './DemoRouteWatcher';
import DemoArrow from './DemoArrow';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const MainLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { brandName } = useBrand();
  const [dealsOpen, setDealsOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [newClientModalOpen, setNewClientModalOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notification count periodically
  useEffect(() => {
    const loadUnread = async () => {
      try {
        const token = localStorage.getItem('mcaToken');
        if (!token) return;
        const res = await axios.get(`${API_BASE}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(res.data.count || 0);
      } catch (e) { /* silent */ }
    };
    loadUnread();
    const interval = setInterval(loadUnread, 30000); // every 30s
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const res = await axios.get(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data || []);
    } catch (e) { /* silent */ }
  };

  const handleBellClick = () => {
    if (!notifOpen) loadNotifications();
    setNotifOpen(!notifOpen);
    setProfileOpen(false);
  };

  const markRead = async (notif) => {
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/notifications/${notif.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    } catch (e) { /* silent */ }
    if (notif.deal_id) {
      navigate(`/deals/${notif.deal_display_number ?? notif.deal_id}`);
      setNotifOpen(false);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* LEFT ICON SIDEBAR */}
      <div className="w-20 bg-white border-r border-gray-200 flex flex-col items-center py-6">
        {/* Logo */}
        <div className="mb-8">
          <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-lg">
            FP
          </div>
        </div>

        {/* Nav Icons */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Deals */}
          <div className="relative">
            <button
              onClick={() => {
                setDealsOpen(!dealsOpen);
                setClientsOpen(false);
              }}
              className={`p-3 rounded-lg transition-colors ${
                isActive('/submitted-deals') || isActive('/funded-deals') || isActive('/archived-deals')
                  ? 'bg-orange-100 text-orange-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Deals"
              data-testid="sidebar-deals-toggle"
            >
              <LayoutDashboard size={24} />
            </button>
            {dealsOpen && (
              <div className="absolute left-20 top-0 ml-2 bg-white shadow-lg rounded-lg py-2 w-48 z-50 border border-gray-200">
                {(user?.role === 'admin' || user?.role === 'underwriter') && (
                  <Link
                    to="/submitted-deals"
                    className="block px-4 py-2 hover:bg-gray-100 text-sm"
                    onClick={() => setDealsOpen(false)}
                  >
                    Submitted Deals
                  </Link>
                )}
                <Link
                  to="/funded-deals"
                  className="block px-4 py-2 hover:bg-gray-100 text-sm"
                  onClick={() => setDealsOpen(false)}
                >
                  Funded Deals
                </Link>
                {(user?.role === 'admin' || user?.role === 'underwriter') && (
                  <Link
                    to="/archived-deals"
                    className="block px-4 py-2 hover:bg-gray-100 text-sm"
                    onClick={() => setDealsOpen(false)}
                  >
                    Archived Deals
                  </Link>
                )}
                {user?.role === 'admin' && (
                  <Link
                    to="/killed-deals"
                    className="block px-4 py-2 hover:bg-red-50 text-sm text-red-700 border-t border-gray-100"
                    onClick={() => setDealsOpen(false)}
                    data-testid="nav-killed-deals"
                  >
                    Killed &amp; Declined
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Clients - Admin/Underwriter Only */}
          {(user?.role === 'admin' || user?.role === 'underwriter') && (
            <div className="relative">
              <button
                onClick={() => {
                  setClientsOpen(!clientsOpen);
                  setDealsOpen(false); // Close deals when clients opens
                }}
                className={`p-3 rounded-lg transition-colors ${
                  isActive('/clients')
                    ? 'bg-orange-100 text-orange-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Clients"
              >
                <Users size={24} />
              </button>
              {clientsOpen && (
                <div className="absolute left-20 top-0 ml-2 bg-white shadow-lg rounded-lg py-2 w-48 z-50 border border-gray-200">
                  <Link
                    to="/clients"
                    className="block px-4 py-2 hover:bg-gray-100 text-sm"
                    onClick={() => setClientsOpen(false)}
                  >
                    View clients
                  </Link>
                  <button 
                    onClick={() => {
                      setClientsOpen(false);
                      setNewClientModalOpen(true);
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                  >
                    Add client
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Companies - Admin/Underwriter */}
          {(user?.role === 'admin' || user?.role === 'underwriter') && (
            <Link
              to="/companies"
              onClick={() => {
                setDealsOpen(false);
                setClientsOpen(false);
              }}
              className={`p-3 rounded-lg transition-colors ${
                isActive('/companies')
                  ? 'bg-orange-100 text-orange-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Companies"
            >
              <Building2 size={24} />
            </Link>
          )}

          {/* Staff - Admin Only */}
          {user?.role === 'admin' && (
            <Link
              to="/staff"
              onClick={() => {
                setDealsOpen(false);
                setClientsOpen(false);
              }}
              className={`p-3 rounded-lg transition-colors ${
                isActive('/staff')
                  ? 'bg-orange-100 text-orange-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Staff Management"
            >
              <Users size={24} />
            </Link>
          )}

          {/* Quotes - Admin/Underwriter Only */}
          {(user?.role === 'admin' || user?.role === 'underwriter') && (
            <Link
              to="/quotes"
              onClick={() => {
                setDealsOpen(false);
                setClientsOpen(false);
              }}
              className={`p-3 rounded-lg transition-colors ${
                isActive('/quotes')
                  ? 'bg-orange-100 text-orange-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Quotes"
            >
              <FileText size={24} />
            </Link>
          )}

          {/* ISO Profile - Admin/Underwriter Only */}
          {(user?.role === 'admin' || user?.role === 'underwriter') && (
            <Link
              to="/iso-profile"
              className={`p-3 rounded-lg transition-colors ${
                isActive('/iso-profile')
                  ? 'bg-orange-100 text-orange-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="ISO Profile"
            >
              <User size={24} />
            </Link>
          )}

          {/* Portfolio Summary */}
          <Link
            to="/portfolio"
            className={`p-3 rounded-lg transition-colors ${
              isActive('/portfolio')
                ? 'bg-orange-100 text-orange-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Portfolio Summary"
          >
            <BarChart3 size={24} />
          </Link>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col">
        {/* DEMO BANNER (only visible for Demo@demo.com) */}
        <DemoBanner />
        <DemoRouteWatcher />
        <DemoArrow />
        {/* TOP HEADER */}
        <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-orange-500 text-white rounded-lg text-lg font-bold" data-testid="header-brand-badge">
              {(brandName || 'D').trim().charAt(0).toUpperCase()}
            </div>
            <span className="text-lg font-semibold text-gray-800" data-testid="header-brand-name">{brandName}</span>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setNewClientModalOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="new-client-btn"
            >
              New Client
            </Button>
            <div className="relative">
              <button 
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg relative"
                onClick={handleBellClick}
                data-testid="notification-bell"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" data-testid="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-12 bg-white shadow-xl rounded-lg w-96 border border-gray-200 z-50 max-h-96 overflow-hidden" data-testid="notification-dropdown">
                  <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
                    <span className="font-semibold text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">Mark all read</button>
                    )}
                  </div>
                  <div className="overflow-y-auto max-h-72">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">No notifications</div>
                    ) : notifications.map(n => (
                      <button
                        key={n.id}
                        onClick={() => markRead(n)}
                        className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition ${!n.read ? 'bg-blue-50' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>}
                          <div className={!n.read ? '' : 'ml-4'}>
                            <p className="text-sm text-gray-800">{n.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{n.type} &middot; {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              <button 
                onClick={() => setProfileOpen(!profileOpen)}
                className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold hover:bg-orange-600"
              >
                {user?.name?.charAt(0) || 'R'}
              </button>
              {profileOpen && (
                <div className="absolute right-0 top-12 bg-white shadow-lg rounded-lg py-2 w-48 border border-gray-200 z-50">
                  <Link
                    to="/iso-profile"
                    className="block px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                    onClick={() => setProfileOpen(false)}
                  >
                    <User size={16} />
                    ISO Profile
                  </Link>
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      className="block px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                      onClick={() => setProfileOpen(false)}
                    >
                      <User size={16} />
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2 text-red-600"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* PAGE CONTENT */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* NEW CLIENT MODAL */}
      <NewClientModal 
        isOpen={newClientModalOpen} 
        onClose={() => setNewClientModalOpen(false)} 
      />
    </div>
  );
};

export default MainLayout;